import { useRouter } from '@tanstack/react-router';
import { useModals, useSnackbars } from '@customerio/pluma-components/react';
import type { ReactNode } from 'react';
import { useAnalytics } from '@/services/analytics';
import { getUserFacingErrorMessage } from '@/services/api/errors';
import {
  useDeleteSqlImport,
  useStartSqlImport,
  useStopSqlImport,
  useUpdateSqlImport,
} from '@/services/db/sql-import/mutations';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { SQL_SYNC_LIST_ROUTE_PATHS } from '../hooks';
import type { SqlImportDraft } from '../new/draft';
import { useSqlImportDetail } from './context';
import { detailsWriteBody } from './edit';
import { DeleteSyncModal } from './components/delete-sync-modal';
import { ToggleStatusModal } from './components/toggle-status-modal';

const UPDATE_FAILED_MESSAGE = "The sync couldn't be updated.";
const DELETE_FAILED_MESSAGE = "The sync couldn't be deleted.";
const TOGGLE_FAILED_MESSAGE = "The sync's status couldn't be changed.";

type UseImportActionsArgs = {
  /** What the sync does, in the progressive form, for the modals. */
  summary: ReactNode;
};

/**
 * The header's actions on the import: renaming it, and the delete and pause/activate modals. The
 * modals are managed, so they get their data and callbacks here and hold no context of their own.
 */
export function useImportActions({ summary }: UseImportActionsArgs) {
  const { workspaceId, type, sqlImportId, sqlImport, markDeleted } = useSqlImportDetail();
  const workspace = Number(workspaceId);
  const modals = useModals();
  const snackbars = useSnackbars();
  const router = useRouter();
  const safeNavigate = useSafeNavigate();
  const { track } = useAnalytics();
  const update = useUpdateSqlImport({ workspaceId: workspace });
  const remove = useDeleteSqlImport({ workspaceId: workspace });
  const start = useStartSqlImport({ workspaceId: workspace });
  const stop = useStopSqlImport({ workspaceId: workspace });

  const report = (id: string, error: unknown, fallback: string) =>
    snackbars.create({ id, variant: 'critical', message: getUserFacingErrorMessage(error, fallback) });

  /** Save a new name or description. Resolves to whether it went through. */
  const updateDetails = async (fields: Partial<Pick<SqlImportDraft, 'name' | 'description'>>): Promise<boolean> => {
    try {
      await update.mutateAsync({ sqlImportId, ...detailsWriteBody(sqlImport, fields, type) });
      return true;
    } catch (error) {
      report('sqlImports.details.error', error, UPDATE_FAILED_MESSAGE);
      return false;
    }
  };

  const deleteSync = async (): Promise<boolean> => {
    try {
      await remove.mutateAsync({ sqlImportId });
      // Unsaved edits on a tab are moot now; the leave guards must not hold the page on a deleted import.
      markDeleted();
      snackbars.create({ id: 'sqlImports.deleted', message: 'Deleted your SQL sync.' });
      safeNavigate(
        router.buildLocation({ to: SQL_SYNC_LIST_ROUTE_PATHS.migrated, params: { workspaceId, type } }).href,
      );
      return true;
    } catch (error) {
      report('sqlImports.delete.error', error, DELETE_FAILED_MESSAGE);
      return false;
    }
  };

  const toggleStatus = async (): Promise<boolean> => {
    try {
      if (sqlImport.active) {
        await stop.mutateAsync({ sqlImportId });
        snackbars.create({ id: 'sqlImports.paused', message: `Paused ${sqlImport.name}` });
      } else {
        await start.mutateAsync({ sqlImportId });
        snackbars.create({ id: 'sqlImports.activated', message: `Activated ${sqlImport.name}` });
      }
      return true;
    } catch (error) {
      report('sqlImports.toggle.error', error, TOGGLE_FAILED_MESSAGE);
      return false;
    }
  };

  return {
    updateDetails,
    openDeleteModal: () => {
      void track('sqlImports', 'syncDeleteStarted', {});
      modals.open(DeleteSyncModal, { name: sqlImport.name, summary, onDelete: deleteSync });
    },
    openToggleStatusModal: () => {
      void track('sqlImports', sqlImport.active ? 'syncPauseStarted' : 'syncActivateStarted', {});
      modals.open(ToggleStatusModal, {
        name: sqlImport.name,
        isActive: sqlImport.active,
        summary,
        onToggle: toggleStatus,
      });
    },
  };
}
